import {
  isRoutineQuestionCard,
  ROUTINE_REACH_OUT_ACTION_TYPE,
  type ActionCardAnalyticsCard,
  type ActionCardSurface,
} from '@littlebird/action-cards';
import {
  delegationPersonName,
  type ActionCard,
  type DelegationPerson,
} from '@littlebird/sdk';
import { useState, type ReactElement } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Command } from '@/components/ui/cmdk/cmdk';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Popover } from '@/components/ui/popover';
import TextArea from '@/components/ui/TextArea';
import { Tooltip } from '@/components/ui/Tooltip';
import {
  actionCardAnalyticsContext,
  actionCardAnalyticsSnapshot,
  operationId,
  trackCardAction,
} from '@/features/action-cards/analytics';
import { cn } from '@/lib/utils';

import {
  useDelegateCardMutation,
  useDelegationTeammatesQuery,
  useReceivedDelegationFor,
} from '../action-card-delegation.query';

const MIN_TEAMMATES_FOR_SEARCH = 6;

// A teammate message belongs to its thread, and only the user answers a question or a routine offer (the server's
// `only_the_user_answers`)
const NON_DELEGATABLE_ACTION_TYPES: ReadonlySet<string> = new Set([
  'teammate_message',
  'question',
  'routine_offer',
  // A routine run asks its own user, in a chat only that user can answer in
  ROUTINE_REACH_OUT_ACTION_TYPE,
]);

type DelegatableCard = ActionCardAnalyticsCard & Pick<ActionCard, 'chatId'>;

type DelegateCardButtonProps = {
  readonly card: DelegatableCard;
  readonly subject: string;
  readonly className?: string;
  readonly trigger?: ReactElement;
  readonly surface?: ActionCardSurface;
};

export const useCanDelegateCard = (
  card: Pick<ActionCard, 'id' | 'actionType' | 'origin' | 'chatId'>
): boolean => {
  const teammates = useDelegationTeammatesQuery().data ?? [];
  const received = useReceivedDelegationFor(card);
  return (
    teammates.length > 0 &&
    !received &&
    !NON_DELEGATABLE_ACTION_TYPES.has(card.actionType) &&
    // A routine run's questions wait on its own user, like its reach-out card
    !isRoutineQuestionCard(card)
  );
};

const delegateErrorMessage = (error: unknown) =>
  error instanceof Error && error.message && !/^HTTP \d/.test(error.message)
    ? error.message
    : "Couldn't send this card. Please try again.";

const initialsOf = (person: DelegationPerson) =>
  delegationPersonName(person)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

export const DelegateCardButton = ({
  card,
  subject,
  className,
  trigger,
  surface = 'home',
}: DelegateCardButtonProps) => {
  const canDelegate = useCanDelegateCard(card);
  const teammates = useDelegationTeammatesQuery().data ?? [];
  const delegate = useDelegateCardMutation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<DelegationPerson>();
  const [note, setNote] = useState('');

  if (!canDelegate) return null;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) return;
    setSelected(undefined);
    setNote('');
  };

  const send = () => {
    if (!selected) return;
    const context = actionCardAnalyticsContext(surface);
    const analytics = actionCardAnalyticsSnapshot(card, context);
    const operation = operationId();
    trackCardAction('delegate', analytics, 'submitted', operation);
    delegate.mutate(
      { cardId: card.id, delegatedToId: selected.id, note: note.trim() },
      {
        onSuccess: () => {
          trackCardAction('delegate', analytics, 'succeeded', operation);
          toast.success(`Sent to ${delegationPersonName(selected)}`);
          handleOpenChange(false);
        },
        onError: (error) => {
          trackCardAction('delegate', analytics, 'failed', operation, {
            outcomeStatus: 'error',
          });
          toast.error(delegateErrorMessage(error));
        },
      }
    );
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      {trigger ? (
        <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      ) : (
        <Tooltip label="Assign to a teammate">
          <Popover.Trigger asChild>
            <Button
              variant="outline"
              size="dense"
              iconOnly
              aria-label={`Assign ${subject} to a teammate`}
              className={cn('shrink-0 bg-surface', className)}
            >
              <Icons.userPlus aria-hidden="true" />
            </Button>
          </Popover.Trigger>
        </Tooltip>
      )}
      <Popover.Content
        align={trigger ? 'start' : 'end'}
        className="w-80 p-0"
        onClick={(event) => event.stopPropagation()}
      >
        <YStack className="gap-0.5 px-3.5 pb-2 pt-3">
          <span className="text-sm font-semibold text-foreground">
            Assign this card
          </span>
          <span className="line-clamp-2 text-xs text-foreground-muted">
            {subject}
          </span>
        </YStack>
        <Command.Root>
          {teammates.length >= MIN_TEAMMATES_FOR_SEARCH ? (
            <Command.Input autoFocus placeholder="Search teammates..." />
          ) : null}
          <Command.List>
            <Command.Empty>
              <span className="text-sm text-foreground-muted">
                No teammates found
              </span>
            </Command.Empty>
            <Command.Group>
              {teammates.map((teammate) => (
                <Command.Item
                  key={teammate.id}
                  value={`${teammate.name ?? ''} ${teammate.email}`}
                  data-current={selected?.id === teammate.id}
                  onSelect={() => setSelected(teammate)}
                >
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-[0.6875rem] font-medium text-foreground-subtle">
                    {initialsOf(teammate)}
                  </span>
                  <YStack className="min-w-0 flex-1 gap-0.5">
                    <span className="truncate text-sm">
                      {delegationPersonName(teammate)}
                    </span>
                    <span className="truncate text-xs text-foreground-muted">
                      {teammate.email}
                    </span>
                  </YStack>
                  {selected?.id === teammate.id ? (
                    <Icons.check className="size-4 shrink-0" />
                  ) : null}
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command.Root>
        {selected ? (
          <YStack className="gap-1.5 border-t border-border-subtle px-3.5 py-3">
            <label
              htmlFor={`delegate-note-${card.id}`}
              className="text-xs text-foreground-muted"
            >
              Note for {delegationPersonName(selected)} (optional)
            </label>
            <TextArea
              id={`delegate-note-${card.id}`}
              value={note}
              rows={2}
              maxLength={2000}
              placeholder="Add context for your teammate"
              className="text-sm"
              onChange={(event) => setNote(event.target.value)}
            />
            <XStack className="justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                isLoading={delegate.isPending}
                loadingText="Sending"
                onClick={send}
              >
                Send
              </Button>
            </XStack>
          </YStack>
        ) : null}
      </Popover.Content>
    </Popover.Root>
  );
};
